import React, { useState } from 'react';
import { Sparkles, Mail, ArrowRight, X } from 'lucide-react';
import { useAccess } from '../../contexts/AccessContext';
import GoogleSignInButton from '../GoogleSignInButton';

const PERKS = [
  'Thousands more shiurim across every speaker',
  'The complete video library and all transcripts',
  'Your favorites, history and downloads, remembered on this device',
];

export const AccessGateModal = () => {
  const { promptOpen, closePrompt, decline, signInWithEmail, isGuest, email: signedEmail, demoted, signinError, clearSigninError } = useAccess();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!promptOpen || !isGuest) return null;

  const shownError = error || signinError;

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setError('Please enter a valid email address.');
      return;
    }
    setBusy(true);
    setError('');
    clearSigninError();
    try {
      await signInWithEmail(clean, name.trim());
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10001] overflow-y-auto overscroll-contain flex items-center justify-center p-4 sm:p-6 bg-[#0f1729]/70 backdrop-blur-sm"
      data-testid="access-gate-modal"
      onClick={(e) => { if (e.target === e.currentTarget) closePrompt(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="access-gate-title"
    >
      <div className="relative w-full max-w-[22rem] sm:max-w-lg my-auto rounded-2xl sm:rounded-3xl overflow-hidden shadow-[0_40px_120px_-20px_rgba(0,0,0,0.7)] border border-[#e5c976]/40 bg-[#fbf7ee] animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div className="relative px-5 sm:px-8 pt-5 sm:pt-7 pb-4 sm:pb-6 text-white" style={{ background: 'linear-gradient(135deg, #0f1729 0%, #14213d 60%, #1c2b4a 100%)' }}>
          <button
            type="button"
            onClick={closePrompt}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="access-gate-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 mb-2 sm:mb-3">
            <img src="/vaad-otzar-logo.jpg" alt="" className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl border border-[#e5c976]/60 object-cover" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#e5c976]">The Vaad Otzar</p>
              <h2 id="access-gate-title" className="text-lg sm:text-2xl font-bold leading-tight">
                {demoted ? 'Welcome back' : 'Welcome to the Otzar'}
              </h2>
            </div>
          </div>
          {demoted ? (
            <p className="text-sm text-[#cfd7ea] leading-relaxed" data-testid="access-gate-demoted-text">
              You&apos;re signed in as <b className="text-white">{signedEmail}</b>, and right now your account has the open library only.
              Please reach out to us if you&apos;d like the full Otzar restored.
            </p>
          ) : (
            <p className="text-[13px] sm:text-sm text-[#cfd7ea] leading-snug sm:leading-relaxed">
              You&apos;re welcome to browse right away. If you&apos;d like the full experience — many more features and
              thousands more shiurim — just leave your email once. No password, nothing to remember.
            </p>
          )}
        </div>

        {!demoted && (
          <div className="px-5 sm:px-8 py-4 sm:py-5">
            <ul className="space-y-0.5 sm:space-y-1 mb-3 sm:mb-4">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-xs sm:text-[13px] text-[#4b5563]">
                  <Sparkles className="w-3.5 h-3.5 text-[#9a7b24] shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <form onSubmit={submit} className="space-y-2" data-testid="access-gate-form">
              <input
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name (optional)"
                className="hidden sm:block w-full px-3 py-2.5 rounded-xl border border-[#e1ddd6] bg-white text-[#0f1729] text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a437]/60"
                data-testid="access-gate-name-input"
              />
              <div className="relative">
                <Mail className="w-4 h-4 text-[#9a7b24] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full pl-9 pr-14 py-2.5 sm:py-3 rounded-xl border border-[#e1ddd6] bg-white text-[#0f1729] text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a437]/60"
                  data-testid="access-gate-email-input"
                />
                <button
                  type="submit"
                  disabled={busy}
                  aria-label="Continue with my email"
                  title="Continue with my email"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-10 h-9 inline-flex items-center justify-center rounded-lg text-white bg-gradient-to-b from-[#c9a437] to-[#8c691b] hover:from-[#e5c976] hover:to-[#9a7523] shadow-[0_4px_12px_-4px_rgba(154,123,36,0.8)] transition-all disabled:opacity-60"
                  data-testid="access-gate-submit"
                >
                  {busy
                    ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    : <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
              {shownError && <p className="text-xs text-red-600" data-testid="access-gate-error">{shownError}</p>}
            </form>

            <div className="flex items-center gap-3 my-2.5 sm:my-3">
              <div className="h-px flex-1 bg-[#e1ddd6]" />
              <span className="text-[11px] uppercase tracking-wider text-[#8a8f99]">or</span>
              <div className="h-px flex-1 bg-[#e1ddd6]" />
            </div>
            <div data-testid="access-gate-google">
              <GoogleSignInButton text="Continue with Google" />
            </div>

            <button
              type="button"
              onClick={decline}
              className="w-full mt-3 sm:mt-4 inline-flex items-center justify-center gap-2 py-2.5 sm:py-3.5 rounded-xl text-sm sm:text-base font-bold text-[#0f1729] bg-white border-2 border-[#0f1729]/30 hover:border-[#0f1729] hover:bg-[#f7f5f0] transition-colors"
              data-testid="access-gate-decline"
            >
              No thanks, continue browsing
            </button>
            <p className="hidden sm:block text-[11px] text-[#8a8f99] text-center mt-2">
              You can always come back to this from the gold button at the top of the page.
            </p>
          </div>
        )}
        {demoted && (
          <div className="px-6 sm:px-8 py-5">
            <button
              type="button"
              onClick={closePrompt}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-[#0f1729] bg-[#f1ece1] hover:bg-[#e8e1d1] transition-colors"
              data-testid="access-gate-demoted-close"
            >
              Continue to the library
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessGateModal;
